/**
 * The bid ladder (rules §3.2) — 25 suit/NT rungs plus Misère and Open Misère.
 *
 * The one thing this file exists to guarantee: **bid legality compares `rank`,
 * never `value`.** Open Misère and 10♥ are both worth 500, but Open Misère sits
 * one rung below 10♥, so 10♥ legally outbids a standing Open Misère
 * (rules §8 edge #11). Anything that sorts on points gets that backwards.
 */
import { SUIT_LABEL } from '../../core/cards.js';
/** Bid-comparison order only — ♠ < ♣ < ♦ < ♥ < NT (rules §3.1.2, §7.2).
 *  This is NOT any in-play card ordering; it exists purely to sequence bids. */
export const STRAIN_ORDER = ['S', 'C', 'D', 'H', 'NT'];
export const LEVELS = [6, 7, 8, 9, 10];
/** 6♠ = 40, each strain step +20, each level step +100 (rules §3.2). */
export function suitBidValue(level, strain) {
    return (level - 6) * 100 + STRAIN_ORDER.indexOf(strain) * 20 + 40;
}
export function strainLabel(strain) {
    return strain === 'NT' ? 'NT' : SUIT_LABEL[strain];
}
export function rungKey(spec) {
    if (spec.kind === 'misere')
        return 'MISERE';
    if (spec.kind === 'openMisere')
        return 'OPEN_MISERE';
    return `${spec.level}${spec.strain}`;
}
export function specLabel(spec) {
    if (spec.kind === 'misere')
        return 'Misère';
    if (spec.kind === 'openMisere')
        return 'Open Misère';
    return `${spec.level}${strainLabel(spec.strain)}`;
}
export function ladderRulesOf(config) {
    return {
        misereValue: config.misereValue,
        miserePlacement: config.miserePlacement,
        openMisereValue: config.openMisereValue,
        openMiserePlacement: config.openMiserePlacement,
    };
}
function baseRungs() {
    const out = [];
    for (const level of LEVELS) {
        for (const strain of STRAIN_ORDER) {
            const spec = { kind: 'suit', level, strain };
            out.push({
                key: rungKey(spec),
                spec,
                tricks: level,
                value: suitBidValue(level, strain),
                label: specLabel(spec),
                category: strain === 'NT' ? 'nt' : 'suit',
            });
        }
    }
    return out;
}
/** Anchor keys: the special rung is spliced in immediately AFTER this rung. */
function misereAnchor(placement) {
    return placement === 'after8S' ? '8S' : '7NT';
}
function openMisereAnchor(placement) {
    if (placement === 'after9NT')
        return '9NT';
    if (placement === 'between10DAnd10H')
        return '10D';
    return null; // 'highest' → appended at the very top
}
const CACHE = new Map();
export function buildLadder(rules) {
    const cacheKey = `${rules.misereValue}|${rules.miserePlacement}|${rules.openMisereValue}|${rules.openMiserePlacement}`;
    const hit = CACHE.get(cacheKey);
    if (hit)
        return hit;
    const seeds = baseRungs();
    const misere = {
        key: 'MISERE',
        spec: { kind: 'misere' },
        tricks: 0,
        value: rules.misereValue,
        label: 'Misère',
        category: 'misere',
    };
    const open = {
        key: 'OPEN_MISERE',
        spec: { kind: 'openMisere' },
        tricks: 0,
        value: rules.openMisereValue,
        label: 'Open Misère',
        category: 'openMisere',
    };
    // Splice by anchor key rather than by numeric index so the two insertions are
    // order-independent, and so a value tie (Open Misère 500 vs 10♥ 500) can
    // never reorder them.
    const insertAfter = (anchorKey, seed) => {
        if (anchorKey === null) {
            seeds.push(seed);
            return;
        }
        const at = seeds.findIndex((s) => s.key === anchorKey);
        seeds.splice(at + 1, 0, seed);
    };
    insertAfter(misereAnchor(rules.miserePlacement), misere);
    insertAfter(openMisereAnchor(rules.openMiserePlacement), open);
    const ladder = Object.freeze(seeds.map((s, i) => Object.freeze({ ...s, rank: i + 1 })));
    CACHE.set(cacheKey, ladder);
    return ladder;
}
export function ladderOf(config) {
    return buildLadder(ladderRulesOf(config));
}
export function rungByKey(ladder, key) {
    return ladder.find((r) => r.key === key) ?? null;
}
export function rungOf(ladder, spec) {
    return rungByKey(ladder, rungKey(spec));
}
/** Rank of the lowest 7-level bid — the threshold for the 'after7' misère
 *  availability option (rules §3.2 options table). */
export function sevenLevelRank(ladder) {
    return rungByKey(ladder, '7S')?.rank ?? 6;
}
//# sourceMappingURL=ladder.js.map